import React, { useState, useRef, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Divider,
  Button,
} from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import PersonIcon from '@mui/icons-material/Person'
import CheckIcon from '@mui/icons-material/Check'

function GeminiAssistant({ firebase, onAcceptSuggestion, initialDescription }) {
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [suggestion, setSuggestion] = useState(null)
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    if (initialDescription && messages.length === 0) {
      const welcomeMessage = {
        role: 'assistant',
        content:
          'Hei! Olen tekoälyavustaja, joka auttaa sinua muotoilemaan ominaisuustoiveesi selkeäksi ja ammattimaiseksi. Kerro mitä toiminnallisuutta tai muutosta kaipaat, niin esitän tarkentavia kysymyksiä.',
      }
      setMessages([welcomeMessage])
    }
  }, [initialDescription, messages.length])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const sendMessage = async () => {
    if (!input.trim() || loading) return

    const userMessage = { role: 'user', content: input }
    const newMessages = [...messages, userMessage]
    setMessages(newMessages)
    setInput('')
    setLoading(true)

    try {
      const history = newMessages.map((m) => ({
        role: m.role,
        content: m.content,
      }))

      const response = await firebase.getGeminiAssistance(input, history)

      if (response.error) {
        setMessages([
          ...newMessages,
          {
            role: 'assistant',
            content: 'Valitettavasti tapahtui virhe. Yritä uudelleen myöhemmin.',
          },
        ])
      } else {
        const assistantMessage = {
          role: 'assistant',
          content: response.message,
        }
        setMessages([...newMessages, assistantMessage])

        if (response.suggestion) {
          setSuggestion(response.suggestion)
        }
      }
    } catch (error) {
      console.error('Gemini error:', error)
      setMessages([
        ...newMessages,
        {
          role: 'assistant',
          content: 'Yhteysvirhe. Tarkista verkkoyhteys ja yritä uudelleen.',
        },
      ])
    } finally {
      setLoading(false)
      // Refocus input after sending
      setTimeout(() => {
        inputRef.current?.focus()
      }, 0)
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const handleAcceptSuggestion = () => {
    if (suggestion && onAcceptSuggestion) {
      onAcceptSuggestion(suggestion)
      setSuggestion(null)
    }
  }

  return (
    <Paper
      elevation={2}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 450,
        maxHeight: 600,
      }}
    >
      <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
        <Box display="flex" alignItems="center">
          <SmartToyIcon sx={{ mr: 1 }} />
          <Typography variant="subtitle1">AI-avustaja</Typography>
        </Box>
        <Typography variant="caption">
          Auttaa muotoilemaan toiveesi selkeäksi
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {messages.map((msg, idx) => (
          <Box
            key={idx}
            sx={{
              display: 'flex',
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                maxWidth: '85%',
              }}
            >
              {msg.role === 'assistant' && (
                <SmartToyIcon
                  sx={{ mr: 1, mt: 0.5, fontSize: 20, color: 'primary.main' }}
                />
              )}
              <Paper
                elevation={1}
                sx={{
                  p: 1.5,
                  bgcolor: msg.role === 'user' ? 'primary.light' : 'grey.100',
                  color: msg.role === 'user' ? 'white' : 'text.primary',
                }}
              >
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {msg.content}
                </Typography>
              </Paper>
              {msg.role === 'user' && (
                <PersonIcon
                  sx={{ ml: 1, mt: 0.5, fontSize: 20, color: 'text.secondary' }}
                />
              )}
            </Box>
          </Box>
        ))}
        {loading && (
          <Box display="flex" alignItems="center" gap={1}>
            <SmartToyIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              AI kirjoittaa...
            </Typography>
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {suggestion && (
        <>
          <Divider />
          <Box sx={{ p: 2, bgcolor: 'success.light' }}>
            <Typography variant="subtitle2" gutterBottom>
              Ehdotettu muotoilu:
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
              <strong>{suggestion.title}</strong>
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
              {suggestion.description}
            </Typography>
            <Button
              variant="contained"
              color="success"
              size="small"
              startIcon={<CheckIcon />}
              onClick={handleAcceptSuggestion}
            >
              Hyväksy ehdotus
            </Button>
          </Box>
        </>
      )}

      <Divider />
      <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          size="small"
          placeholder="Kirjoita viesti..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={loading}
          multiline
          maxRows={3}
          autoFocus
        />
        <IconButton
          color="primary"
          onClick={sendMessage}
          disabled={!input.trim() || loading}
        >
          <SendIcon />
        </IconButton>
      </Box>
    </Paper>
  )
}

export default GeminiAssistant
